import { Controller as BaseController } from 'stimulus'
import template from 'lodash.template'
import I18n from '@app/javascript/i18n/index.js.erb'
import { setCookie } from '@app/javascript/classes/cookies'

export class Controller extends BaseController {
  static targets = [
    'activeSessionsToggler',
    'activeSessionsPillTemplate',
    'activeSessionsPill'
  ]

  static values = { activeSessions: Array }

  connect() {
    this.activeSessionsPillTemplate = template(this.activeSessionsPillTemplateTarget.innerHTML)
    this.activeSessionsPillContainer = this.activeSessionsPillTemplateTarget.parentElement
  }

  activeSessionsToggledByUser() {
    if (this.isActiveSessionsTogglerSwitchedOn()) {
      this.selectActiveSessionsOnly()
      this.rememberActiveSessionsSelection({ value: true })
    } else {
      this.deselectActiveSessions()
      this.rememberActiveSessionsSelection({ value: false })
    }
  }

  isActiveSessionsTogglerSwitchedOn() {
    return this.activeSessionsTogglerTarget.checked
  }

  rememberActiveSessionsSelection({ value }) {
    const domain = window.location.hostname === 'localhost' ? 'localhost' : '.fiscalnote.com'
    setCookie('searchPage.activeSessions', value, { longLasting: true, path: '/', domain: domain })
  }

  selectActiveSessionsOnly() {
    this.selectMultipleComponent.clearAll({ silent: true })
    this.selectMultipleComponent.selectValues(this.activeSessions)
    this.selectMultipleComponent.setItemsAsNonSubmittable()
    this.selectMultipleComponent.hideSelectionsList()
    this.showActiveSessionsPill()
  }

  deselectActiveSessions() {
    if (this.selectMultipleItemsHasBeenSubmitted) {
      this.selectMultipleItemsHasBeenSubmitted = false
      return
    }

    this.hideActiveSessionsPill()
    this.selectMultipleComponent.deselectValues(this.activeSessions)
    this.selectMultipleComponent.restoreItemsAsSubmittable()
    this.selectMultipleComponent.showSelectionsList()
  }

  showActiveSessionsPill() {
    const valuesSelected = this.selectMultipleComponent.getSelectedValues()
    const activeSessionsSelected = valuesSelected.filter((value) => {
      return this.activeSessions.includes(value)
    })

    if (activeSessionsSelected.length === 0) {
      this.hideActiveSessionsPill()
      return
    }

    const pill = this.activeSessionsPillTemplate({
      text: I18n.t(
        'search_v2.filters.policy_legislation_session.active_sessions_pill',
        { count: activeSessionsSelected.length }
      )
    })

    this.activeSessionsPillContainer.innerHTML = pill
  }

  hideActiveSessionsPill() {
    this.activeSessionsPillContainer.innerHTML = ''
  }

  onSelectMultipleSubmitted() {
    this.selectMultipleComponent.restoreItemsAsSubmittable()
    this.selectMultipleComponent.showSelectionsList()
    this.setActiveSessionsTogglerOff()

    if (this.hasActiveSessionsPillTarget) {
      this.selectMultipleItemsHasBeenSubmitted = true
      this.hideActiveSessionsPill()
    }
  }

  onSelectMultipleItemsReloaded() {
    this.selectMultipleItemsReloadedCount ||= 0
    this.selectMultipleItemsReloadedCount += 1

    // The first two 'reload' events are fired when the Leg Sessions list is being loaded
    // for the first time (yikes). After that, we can assume that the items are
    // actually being reloaded.
    const isSelectMultipleItemsBeingReloaded = this.selectMultipleItemsReloadedCount > 2

    if (this.isActiveSessionsTogglerSwitchedOn()) {
      this.selectActiveSessionsOnly()

      if (this.hasActiveSessionsPillTarget) {
        this.selectMultipleItemsHasBeenReloaded = isSelectMultipleItemsBeingReloaded
      }
    }
  }

  onActiveSessionsPillRemoved() {
    if (this.selectMultipleItemsHasBeenReloaded) {
      this.selectMultipleItemsHasBeenReloaded = false
      return
    }

    this.setActiveSessionsTogglerOff()
    this.deselectActiveSessions()
    this.rememberActiveSessionsSelection({ value: false })
  }

  setActiveSessionsTogglerOff() {
    this.activeSessionsTogglerTarget.checked = false
  }

  get activeSessions() {
    return this.activeSessionsValue
  }

  get selectMultipleComponent() {
    return this.element.querySelector('[data-controller="select-multiple-component"]').controllers['select-multiple-component']
  }
}
